function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

/* eslint-disable class-methods-use-this, consistent-return */
const CollectionLocalizerFactory = require('../entities/collection-localizer');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const _ = require('lodash');

const AccessContext = require('../repositories/contexts/access-context');

const filterQueries = require('../utils/filterQueries/filterQueries');

const updateGeoIds = require('./collection/update-geoids');

const serializeMeta = regionFilters => {
  const included = new Set();
  const excluded = new Set();

  _.get(regionFilters, 'filters', []).forEach(filter => {
    if (filter.target !== 'regions') return;
    if (filter.operation === 'discoverableIn') return filter.args.forEach(r => included.add(r.id));
    if (filter.operation === 'notDiscoverableIn') filter.args.forEach(r => excluded.add(r.id));
  });

  return {
    regions: {
      included: Array.from(included),
      excluded: Array.from(excluded)
    }
  };
};

class CollectionUpdatePipeline {
  constructor(client
  /*: ApiClient*/
  , context
  /*: AccessContext*/
  ) {
    if (!client) throw new Error('Cannot create CollectionUpdatePipeline without client instance');
    if (!context) throw new Error('Cannot create CollectionUpdatePipeline without context');
    this.client = client;
    this.context = context;
    this.instances = {
      filterCategories: []
    };
  }

  setInstance(key
  /*: string*/
  , instance
  /*: any*/
  ) {
    this.instances[key] = instance;
  }

  getInstance(key
  /*: string*/
  )
  /*: **/
  {
    return this.instances[key];
  } // Forward compatibility with EditPipeline


  getFields() {
    return this.instances;
  }

  update(updates
  /*: Object*/
  ) {
    const _this = this;

    return _call(function () {
      // Create necessary schedules
      return _await(_this.updateSchedules(updates), function () {
        // Core model updates
        return _await(_this.updateRegionFilters(updates), function () {
          return _await(_this.updateCategories(updates), function () {
            return _await(_this.createOrUpdate(updates), function () {
              return _await(_this.updateLocalizations(updates), function () {
                // Related model updates
                return _await(_this.updateSlug(updates), function () {
                  return _this;
                });
              });
            });
          });
        });
      });
    });
  } // "Private" methods


  createOrUpdate(updates
  /*: Object*/
  ) {
    const _this2 = this;

    return _call(function () {
      const collection = _this2.getInstance('collection');

      let updatedCollectionProcess;

      const {
        propertyId
      } = _this2.context.serialize();

      const locales = _this2.getInstance('locales');

      const collectionUpdates = CollectionLocalizerFactory(locales).applyDefaultLocalizations(updates.collection);
      collectionUpdates.businessFilter = filterQueries.filterSetToQuery(updates.businessFilter);
      collectionUpdates.businessOfferFilter = filterQueries.filterSetToQuery(updates.businessOfferFilter);

      if (collection.id) {
        updatedCollectionProcess = _this2.client.properties.for(propertyId).collections.coreUpdate(collection, collectionUpdates);
      } else {
        updatedCollectionProcess = _this2.client.properties.for(propertyId).collections.coreCreate(collectionUpdates);
      }

      return _await(updatedCollectionProcess.then(updatedCollection => {
        _this2.setInstance('collection', updatedCollection);
      }));
    });
  }

  updateLocalizations(updates
  /*: Object*/
  ) {
    const _this3 = this;

    return _call(function () {
      const collection = _this3.getInstance('collection');

      const collectionUpdates = updates.collection;

      const locales = _this3.getInstance('locales');

      const {
        propertyId
      } = _this3.context.serialize();

      const localizations = CollectionLocalizerFactory(locales).getLocalizations(collectionUpdates);

      const collectionRepo = _this3.client.properties.for(propertyId).collections.for(collection.id);

      return Promise.all(localizations.map(({
        locale,
        content
      }) => collectionRepo.setLocalization({
        locale,
        content
      })));
    });
  }

  mergePolygons({
    include,
    exclude
  }) {
    const _this4 = this;

    return _call(function () {
      return include.length ? _await(_this4.client.properties.for(null) // FIXME: Don't do this
      .collections.mergeGeoShapes({
        include,
        exclude
      })) : _await([]);
    });
  }

  updateRegionFilters(updates
  /*: Object*/
  ) {
    const _this5 = this;

    return _call(function () {
      const regionFilterUpdates = updates.regionFilters;
      const collectionUpdates = updates.collection;

      const collection = _this5.getInstance('collection');

      const newMeta = serializeMeta(regionFilterUpdates);
      const existingMeta = collection.meta;
      const newExcluded = newMeta.regions.excluded;
      const newIncluded = newMeta.regions.included;
      const excludedChanged = !!_.xor(newExcluded, _.get(existingMeta, 'regions.excluded', [])).length;
      const includedChanged = !!_.xor(newIncluded, _.get(existingMeta, 'regions.included', [])).length;
      if (!excludedChanged && !includedChanged) return _await();
      collectionUpdates.meta = newMeta;
      return _await();
    });
  }

  updateCategories(updates
  /*: Object*/
  ) {
    return _call(function () {
      const categories = updates.filterCategories;
      if (!categories) return _await();
      const collection = updates.collection;
      collection.filterCategories = categories.map(c => c.id);
      return _await();
    });
  }

  updateSlug(updates
  /*: Object*/
  ) {
    const _this6 = this;

    return _call(function () {
      const collection = _this6.getInstance('collection');

      const {
        propertyId
      } = _this6.context.serialize();

      return _await(_this6.client.properties.for(propertyId).collections.updateSlug(collection, updates.collection), function (updatedSlug) {
        // TODO: What's the correct pattern for updating instance fields?
        collection.slug = updatedSlug;
      });
    });
  }

  updateSchedules(updates
  /*: Object*/
  ) {
    const _this7 = this;

    return _call(function () {
      const schedules = updates.schedules;

      const collection = _this7.getInstance('collection');

      return _await(Promise.all(schedules.map(schedule => {
        if (schedule.allDates && schedule.allWeekDays && schedule.allTimes) return null; // Create ALL schedules in case they're changed
        // TODO: Diff schedules and only re-create those that are changed

        return _this7.client.schedules.create(schedule, true);
      })), function (updatedSchedules) {
        const scheduleIds = updatedSchedules.filter(Boolean).map(schedule => schedule.id);
        collection.scheduleIds = scheduleIds; // eslint-disable-next-line no-param-reassign

        updates.collection.scheduleIds = scheduleIds; // gross, but necessary until we refactor =[

        _this7.setInstance('collection', collection);
      });
    });
  }

  updateBusinessGeoIds(collectionFields
  /*: Object*/
  , geoIds
  /*: Array<GeoId>*/
  ) {
    const filter = collectionFields.businessOfferFilter;
    const updatedFilter = updateGeoIds(filter, 'businessId', geoIds);
    /* eslint-disable-next-line no-param-reassign */

    collectionFields.businessOfferFilter = updatedFilter;
  }

  updateBusinessOfferGeoIds(collectionFields
  /*: Object*/
  , geoIds
  /*: Array<GeoId>*/
  ) {
    const filter = collectionFields.businessOfferFilter;
    const updatedFilter = updateGeoIds(filter, 'businessOfferIds', geoIds);
    /* eslint-disable-next-line no-param-reassign */

    collectionFields.businessOfferFilter = updatedFilter;
  }

}

module.exports = CollectionUpdatePipeline;